import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { SchedulerDetailsTypes } from '../SchedulerDetails.types';
import moment from 'moment-timezone';
import { useWeekScheduleFetch } from './useWeekScheduleFetchV2';
import { isEqual } from 'lodash-es';

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// Constants
const CACHE_TIMEOUT = 1 * 60 * 1000; // 1 minute

// Helper function to format week range
const formatWeekRange = (start: moment.Moment, end: moment.Moment): string => {
  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      return `${start.format('MMM D')} – ${end.format('D, YYYY')}`;
    } else {
      return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }
  } else {
    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
  }
};

// Helper to format ISO string
const formatWeekISO = (
  date: moment.Moment | null,
  originalFormat: string
): string | null => {
  // ... (original code)
  if (!date) return null;
  // Basic check for Z or offset in original format
  if (
    originalFormat.includes('Z') &&
    !originalFormat.includes('+') &&
    !originalFormat.includes('-')
  ) {
    return date.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'); // UTC 'Z' format
  } else if (
    originalFormat.match(/[+-]\d{2}:\d{2}$/) ||
    originalFormat.match(/[+-]\d{4}$/)
  ) {
    return date.format('YYYY-MM-DDTHH:mm:ssZ'); // Format with offset like +05:30
  } else {
    // Default or guess based on moment's default ISO format with offset
    return date.toISOString(true); // Keep local time, add offset
  }
};

/**
 * Custom hook to manage scheduling logic for interviewers with week-based data fetching.
 * Fetches current week data actively and next week data proactively.
 * Handles navigation and ensures subsequent week data is fetched.
 */
export const useScheduleLogic = ({
  timeZone, // Default to browser timezone if undefined
  interviewerIds = [],
  currentWeekRange: currentWeekRangeFromProp, // Rename prop to avoid clash
  interviewDataRef,
  jobId,
  jobStageActivityId,
  registerForUpdates,
  updateSharedData,
  getNextWeek = true, // Default to always get next week
  isBulkManual = false,
}: {
  timeZone?: string;
  interviewerIds?: (string | number)[];
  currentWeekRange?: string; // The range string from FullCalendar
  interviewDataRef?: any;
  jobId?: number;
  jobStageActivityId?: string | number;
  registerForUpdates: (key: string, callback: (arg: any) => void) => () => void;
  updateSharedData: (data: Record<string, any>) => void; // Use specific type
  getNextWeek?: boolean;
  isBulkManual?: boolean;
}) => {
  // --- State and Refs ---
  const [interviewersData, setInterviewersData] = useState<
    Record<string, Record<string, SchedulerDetailsTypes.InterviewerData>>
  >({});

  // Track which weeks have been fetched or are being fetched
  const fetchedWeeksRef = useRef<Set<string>>(new Set());

  // Refs for caching and tracking - these are shared across week fetches
  const tempCacheRef = useRef<
    Record<string, Record<string, SchedulerDetailsTypes.InterviewerData>>
  >({});
  const timeoutIdsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Keep track of the last viewed week
  const lastViewedWeekRef = useRef<string | undefined>(undefined);

  // Track which weeks have been fetched for which interviewers
  const [weekInterviewerFetchStatus, setWeekInterviewerFetchStatus] = useState<
    Record<string, Set<string>>
  >({});

  const [localInterviewerIds, setLocalInterviewerIds] =
    useState(interviewerIds);
  const [localDatesSet, setLocalDatesSet] = useState(
    interviewDataRef?.current?.datesSet
  );

  // ADDED: Track previous interviewer IDs to detect removals
  const prevLocalInterviewerIds = usePrevious(localInterviewerIds);

  // ADDED: Ref to track if initial data for bulk manual mode has been loaded for the current step
  const initialCacheLoadAttemptedForStepRef = useRef<string | number | null>(
    null
  );

  // NEW: State to track if cache check has completed for the current stable context
  const [
    cacheCheckCompletedForCurrentContext,
    setCacheCheckCompletedForCurrentContext,
  ] = useState(false);

  // Mark interviewers as fetched, ensuring relevance and immutability
  const markInterviewersFetched = useCallback(
    (weekRange: string, interviewersJustFetched: (string | number)[]) => {
      if (!weekRange || interviewersJustFetched.length === 0) return;

      // Get the set of interviewers currently required
      const currentRequiredIdSet = new Set(localInterviewerIds.map(String));

      setWeekInterviewerFetchStatus((prev) => {
        const existingWeekSet = prev[weekRange] || new Set<string>();
        let changed = false;
        const newWeekSet = new Set(existingWeekSet); // Clone for immutability

        interviewersJustFetched.forEach((id) => {
          const idStr = String(id);
          // Only add if the interviewer is *still* required and *not already* marked fetched
          if (currentRequiredIdSet.has(idStr) && !newWeekSet.has(idStr)) {
            newWeekSet.add(idStr);
            changed = true;
          }
        });

        // Return new outer object only if the inner set changed
        return changed ? { ...prev, [weekRange]: newWeekSet } : prev;
      });
    },
    [localInterviewerIds] // ADDED localInterviewerIds dependency
  );

  // MODIFIED: Helper to check if a week has all necessary interviewers fetched
  const isWeekFullyFetched = useCallback(
    (weekRange: string) => {
      // ADDED: Handle case where no interviewers are needed or no status exists
      if (
        !weekRange ||
        localInterviewerIds.length === 0 ||
        !weekInterviewerFetchStatus[weekRange]
      ) {
        return false;
      }
      const fetchedSet = weekInterviewerFetchStatus[weekRange];
      // Check if all *current* interviewer IDs (as strings) are in the fetched set
      return localInterviewerIds.every((id) => fetchedSet.has(String(id)));
    },
    [localInterviewerIds, weekInterviewerFetchStatus] // Dependencies unchanged
  );

  // --- Cache Clearing ---
  const clearCache = useCallback((key?: string) => {
    if (key) {
      const [weekRange, id] = key.split('_');
      // Clear from temp cache
      if (tempCacheRef.current[weekRange]?.[id]) {
        delete tempCacheRef.current[weekRange][id];
        if (Object.keys(tempCacheRef.current[weekRange]).length === 0) {
          delete tempCacheRef.current[weekRange];
        }
      }
      // Clear timeout
      if (timeoutIdsRef.current[key]) {
        clearTimeout(timeoutIdsRef.current[key]);
        delete timeoutIdsRef.current[key];
      }
    } else {
      // Clear all timeouts
      Object.values(timeoutIdsRef.current).forEach(clearTimeout);
      timeoutIdsRef.current = {};
      // Clear entire temp cache
      tempCacheRef.current = {};
    }
  }, []);

  // --- Event Listeners for External Updates ---
  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewers',
      (data) => {
        const { interviewers } = data?.fullData ?? {};
        // ADDED: Ensure interviewers is an array before setting
        const newIds = Array.isArray(interviewers) ? interviewers : [];
        setLocalInterviewerIds(
          (prev) => (isEqual(prev, newIds) ? prev : newIds) // Use newIds
        );
      }
    );
    const unregisterDatesSet = registerForUpdates('datesSet', (data) => {
      const { datesSet } = data?.fullData ?? {};
      setLocalDatesSet((prev) => (isEqual(prev, datesSet) ? prev : datesSet));
    });

    return () => {
      unregisterInterviewers();
      unregisterDatesSet();
    };
  }, [registerForUpdates]);

  // --- Effect to clear fetch status for removed interviewers ---
  useEffect(() => {
    // Only run if prevLocalInterviewerIds is available (not initial render) and IDs changed
    if (
      Array.isArray(prevLocalInterviewerIds) &&
      !isEqual(prevLocalInterviewerIds, localInterviewerIds)
    ) {
      const currentIdsSet = new Set(localInterviewerIds.map(String));
      const removedIds = prevLocalInterviewerIds.filter(
        (id) => !currentIdsSet.has(String(id))
      );

      if (removedIds.length > 0) {
        // Update status state immutably
        setWeekInterviewerFetchStatus((prevStatus) => {
          const newStatus = { ...prevStatus }; // Shallow copy weeks
          let weekStatusChanged = false;
          removedIds.forEach((removedId) => {
            const removedIdStr = String(removedId);
            Object.keys(newStatus).forEach((weekRange) => {
              if (newStatus[weekRange]?.has(removedIdStr)) {
                // Clone the Set for the specific week before modifying
                const updatedSet = new Set(newStatus[weekRange]);
                updatedSet.delete(removedIdStr);
                newStatus[weekRange] = updatedSet; // Assign the modified Set
                weekStatusChanged = true;
                // Optional: remove weekRange key if set becomes empty
                // if (updatedSet.size === 0) {
                //     delete newStatus[weekRange];
                // }
              }
            });
          });
          // Return new object only if something actually changed
          return weekStatusChanged ? newStatus : prevStatus;
        });
      }
    }
  }, [localInterviewerIds, prevLocalInterviewerIds]); // Depend on current and previous IDs

  // --- Week Range Calculation ---
  const currentViewStartStr = localDatesSet?.startStr;
  const currentViewOriginalFormat = localDatesSet?.startStr || '';
  const currentViewStartMoment = currentViewStartStr
    ? moment.tz(currentViewStartStr, timeZone)
    : null;
  const actualCurrentWeekStart = currentViewStartMoment
    ? currentViewStartMoment.clone().startOf('week') // Start of the week (e.g., Sunday)
    : null;

  // 2. Calculate reliable week range string and dates for the CURRENT week
  const currentWeekStart = actualCurrentWeekStart;
  const currentWeekEnd = currentWeekStart
    ? currentWeekStart.clone().endOf('week')
    : null;
  const currentWeekRange =
    currentWeekStart && currentWeekEnd
      ? formatWeekRange(currentWeekStart, currentWeekEnd)
      : undefined;
  const currentWeekStartISO = currentWeekStart
    ? formatWeekISO(currentWeekStart, currentViewOriginalFormat)
    : null;
  const currentWeekEndISO = currentWeekEnd
    ? formatWeekISO(currentWeekEnd, currentViewOriginalFormat)
    : null;

  // 3. Calculate reliable week range string and dates for the NEXT week
  const nextWeekStart = currentWeekStart
    ? currentWeekStart.clone().add(1, 'week')
    : null;
  const nextWeekEnd = nextWeekStart
    ? nextWeekStart.clone().endOf('week')
    : null;
  const nextWeekRange =
    nextWeekStart && nextWeekEnd
      ? formatWeekRange(nextWeekStart, nextWeekEnd)
      : undefined;
  const nextWeekStartISO = nextWeekStart
    ? formatWeekISO(nextWeekStart, currentViewOriginalFormat)
    : null;
  const nextWeekEndISO = nextWeekEnd
    ? formatWeekISO(nextWeekEnd, currentViewOriginalFormat)
    : null;

  // Effect to reset cacheCheckCompletedForCurrentContext when key dependencies change
  useEffect(() => {
    if (isBulkManual) {
      setCacheCheckCompletedForCurrentContext(false);
      if (
        jobStageActivityId &&
        initialCacheLoadAttemptedForStepRef.current !== jobStageActivityId
      ) {
        initialCacheLoadAttemptedForStepRef.current = null;
      }
    } else {
      setCacheCheckCompletedForCurrentContext(true);
      initialCacheLoadAttemptedForStepRef.current = null;
    }
  }, [isBulkManual, jobStageActivityId, currentWeekRange, localInterviewerIds]);

  // --- Effect to initialize interviewersData from userEventsInfo if isBulkManual ---
  useEffect(() => {
    // Only proceed if critical data for cache lookup is available
    if (
      !isBulkManual ||
      !jobStageActivityId ||
      !currentWeekRange ||
      localInterviewerIds.length === 0
    ) {
      return; // Exit if dependencies for a stable context are not yet met
    }

    // If we've already attempted to load cache for this specific step ID, OR if cache check has already completed for this exact context
    if (initialCacheLoadAttemptedForStepRef.current === jobStageActivityId) {
      // If already attempted for this step, ensure completion flag is true if it wasn't already.
      if (!cacheCheckCompletedForCurrentContext)
        setCacheCheckCompletedForCurrentContext(true);
      return;
    }

    const sharedUserEventsInfo = (interviewDataRef?.current as any)
      ?.userEventsInfo as
      | Record<string, Record<string, SchedulerDetailsTypes.InterviewerData>>
      | undefined;

    if (sharedUserEventsInfo) {
      const dataForCurrentWeekFromShared =
        sharedUserEventsInfo[currentWeekRange];

      if (
        dataForCurrentWeekFromShared &&
        Object.keys(dataForCurrentWeekFromShared).length > 0
      ) {
        const hasDataForAllSelectedInterviewersInShared =
          localInterviewerIds.every((id) =>
            Object.prototype.hasOwnProperty.call(
              dataForCurrentWeekFromShared,
              String(id)
            )
          );

        if (hasDataForAllSelectedInterviewersInShared) {
          const dataToUseForWeek = dataForCurrentWeekFromShared;
          const currentLocalDataForWeek =
            interviewersData[currentWeekRange] || {};
          if (!isEqual(currentLocalDataForWeek, dataToUseForWeek)) {
            setInterviewersData((prevData) => ({
              ...prevData,
              [currentWeekRange!]: { ...dataToUseForWeek },
            }));
          }

          markInterviewersFetched(currentWeekRange, localInterviewerIds);
        }
      }
    }

    initialCacheLoadAttemptedForStepRef.current = jobStageActivityId;
    // CRITICAL: Mark that the cache check for THIS current, stable context has completed.
    setCacheCheckCompletedForCurrentContext(true);
  }, [
    isBulkManual,
    jobStageActivityId,
    currentWeekRange,
    localInterviewerIds,
    interviewDataRef,
    setInterviewersData,
    interviewersData,
    markInterviewersFetched,
    cacheCheckCompletedForCurrentContext,
  ]);

  // Separate effect for resetting the ref when not in bulk manual mode
  useEffect(() => {
    if (!isBulkManual) {
      initialCacheLoadAttemptedForStepRef.current = null;
    }
  }, [isBulkManual]);

  // --- Effect to update shared data with interviewersData if isBulkManual ---
  useEffect(() => {
    if (isBulkManual) {
      const currentSharedUserEventsInfo =
        interviewDataRef?.current?.userEventsInfo;
      if (!isEqual(currentSharedUserEventsInfo, interviewersData)) {
        updateSharedData({
          userEventsInfo: interviewersData,
        });
      }
    }
  }, [isBulkManual, interviewersData, updateSharedData, interviewDataRef]);

  // --- Determine Fetching Eligibility ---
  const canFetch = timeZone && timeZone !== 'local' && !!jobId;

  // Track when current week changes
  useEffect(() => {
    if (currentWeekRange && currentWeekRange !== lastViewedWeekRef.current) {
      lastViewedWeekRef.current = currentWeekRange;
    }
  }, [currentWeekRange]);

  // --- Fetch Flags --- (MODIFIED nextWeek check)
  const shouldEnableFetch = isBulkManual
    ? cacheCheckCompletedForCurrentContext
    : true;

  // Check if current week data actually exists and is complete
  const hasCompleteCurrentWeekData = useMemo(() => {
    if (!currentWeekRange || localInterviewerIds.length === 0) return false;
    const weekData = interviewersData[currentWeekRange] || {};
    const result = localInterviewerIds.every((id) => {
      const data = weekData[String(id)];
      return (
        data &&
        data.busyEvents !== undefined &&
        data.calenderConnected !== undefined
      );
    });
    return result;
  }, [currentWeekRange, localInterviewerIds, interviewersData]);

  const shouldFetchCurrentWeek =
    shouldEnableFetch &&
    canFetch &&
    !!currentWeekRange &&
    localInterviewerIds.length > 0 &&
    (!isWeekFullyFetched(currentWeekRange) || !hasCompleteCurrentWeekData);

  const shouldFetchNextWeek =
    shouldEnableFetch &&
    getNextWeek &&
    canFetch &&
    !!nextWeekRange &&
    localInterviewerIds.length > 0 &&
    !isWeekFullyFetched(nextWeekRange);

  // --- Instantiate Fetch Hooks ---

  // Fetch for the CURRENT week
  const {
    isLoading: isCurrentWeekLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interviewersToFetch: currentWeekInterviewersToFetch,
  } = useWeekScheduleFetch({
    isCurrentWeek: true,
    weekRange: currentWeekRange!,
    weekStartISO: currentWeekStartISO,
    weekEndISO: currentWeekEndISO,
    allInterviewerIds: localInterviewerIds,
    jobId,
    timeZone,
    isEnabled: shouldFetchCurrentWeek,
    interviewersData,
    setInterviewersData,
    updateSharedData,
    tempCacheRef,
    timeoutIdsRef,
    clearCache,
    CACHE_TIMEOUT,
    interviewDataRef,
    onFetchStart: () => {
      if (currentWeekRange) {
        fetchedWeeksRef.current.add(currentWeekRange);
      }
    },
    onFetchComplete: (fetchedInterviewers) => {
      // Uses updated markInterviewersFetched
      if (currentWeekRange && fetchedInterviewers?.length > 0) {
        markInterviewersFetched(currentWeekRange, fetchedInterviewers);
      }
    },
  });

  // Next week fetch
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading: isNextWeekLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interviewersToFetch: nextWeekInterviewersToFetch,
  } = useWeekScheduleFetch({
    weekRange: nextWeekRange!,
    weekStartISO: nextWeekStartISO,
    weekEndISO: nextWeekEndISO,
    allInterviewerIds: localInterviewerIds,
    jobId,
    timeZone,
    isEnabled: shouldFetchNextWeek,
    interviewersData,
    setInterviewersData,
    updateSharedData,
    tempCacheRef,
    timeoutIdsRef,
    clearCache,
    CACHE_TIMEOUT,
    interviewDataRef,
    onFetchStart: () => {
      if (nextWeekRange) {
        fetchedWeeksRef.current.add(nextWeekRange);
      }
    },
    onFetchComplete: (fetchedInterviewers) => {
      // Uses updated markInterviewersFetched
      if (nextWeekRange && fetchedInterviewers?.length > 0) {
        markInterviewersFetched(nextWeekRange, fetchedInterviewers);
      }
    },
  });

  // --- Combined Loading State ---
  const isLoading = isCurrentWeekLoading;

  // --- Return Values ---
  return {
    currentWeekData: currentWeekRange
      ? interviewersData[currentWeekRange] || {}
      : {},
    nextWeekData: nextWeekRange ? interviewersData[nextWeekRange] || {} : {},
    currentWeekRange,
    nextWeekRange,
    interviewersData,
    clearCache,
    isLoading,
  };
};
